import type { Race } from "../hooks/use-f1-data";

/**
 * Returns the best default race to show:
 * 1. The next upcoming race with fantasy data
 * 2. Else the next upcoming race (even without data — server fetches on-demand)
 * 3. Else the latest past race with data
 *
 * This is the canonical "default race" selection logic used by route loaders.
 */
export function getNextOrLatestRace(races: Race[]): Race | null {
  if (races.length === 0) return null;

  const now = new Date();

  // Prefer next upcoming race with data
  const upcomingWithData = races
    .filter((r) => r.hasFantasyData && r.date != null && new Date(r.date) > now)
    .sort((a, b) => new Date(a.date!).getTime() - new Date(b.date!).getTime());
  if (upcomingWithData.length > 0) return upcomingWithData[0];

  // Fall back to next upcoming race (server will seed on-demand)
  const nextUpcoming = races
    .filter((r) => r.date != null && new Date(r.date) > now)
    .sort((a, b) => new Date(a.date!).getTime() - new Date(b.date!).getTime());
  if (nextUpcoming.length > 0) return nextUpcoming[0];

  // All past — return the latest race with data
  const withData = races.filter((r) => r.hasFantasyData);
  return withData[withData.length - 1] ?? null;
}
